import { normIcao } from "./airportFrequencyLocal";
import type { FlightLeg } from "./types";

/**
 * Airports that strongly indicate a Utah Gas Corp / operating leg (override “time share” in notes).
 * Extend this list as you add rules (ICAO, uppercased).
 */
const BUSINESS_ANCHOR_ICAO_RAW = ["KGJT"];

const BUSINESS_ANCHOR_ICAO = new Set(BUSINESS_ANCHOR_ICAO_RAW.map((c) => normIcao(c)));

export function businessAnchorIcaoSet(): ReadonlySet<string> {
  return BUSINESS_ANCHOR_ICAO;
}

/** ForeFlight / worksheet convention for personal timeshare legs. */
export function textIndicatesTimeShare(text: string): boolean {
  return /\btime\s*share\b/i.test(text);
}

function combinedNarrative(leg: FlightLeg): string {
  return [leg.routeNotes, leg.passengerList].filter((s) => (s ?? "").trim()).join("\n");
}

/**
 * Heuristic business signals in free text (route + passenger list).
 * Kept broad for your log phrasing; “time share” is handled separately and checked first.
 */
export function textSuggestsBusiness(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  const patterns: RegExp[] = [
    /\bBusiness\b/i,
    /\bMeet\b/i,
    /\bUGC\b/i,
    /\bboard\s+meeting\b/i,
    /\bcapex\b/i,
    /\bfinancials?\b/i,
    /\bHonda\s+MX\b/i,
    /\bMX\s+at\b/i,
    /\bMX\b.*\b(Elevate|test|maintenance)\b/i,
    /\bmaintenance\b/i,
    /\bemergency\b.*\bBusiness\b/i,
    /\bdiscuss\b/i,
    /\breview\b/i,
    /\bWexpro\b/i,
    /\bWhite\s+Tank\b/i,
    /\bPinnacle\b/i,
    /\bPark\s+Canyon\b/i,
    /\bAEC\b/i,
    /\by-grade\b/i,
    /\bJPM\b/i,
    /\bliquids\s+recovery\b/i,
    /\bRangely\b/i,
    /\bDragon\s+Trail\b/i,
    /\bgathering\b/i,
    /\bpipe\s+construction\b/i,
    /\bhaz\s*mat\b/i,
    /\bprocedures\b/i,
    /\boperat(ing|ions)\b/i,
    /\bmarketing\b/i,
    /\bsnowville\b/i,
    /\bharvest\b/i,
    /\bnatural\s+gas\s+sales\b/i,
  ];
  return patterns.some((re) => re.test(t));
}

export type LegRollupClassification = {
  /** True = count leg as personal for worksheet rollup. */
  personal: boolean;
  /** Short audit trail for UI / support. */
  reasons: string[];
};

/**
 * Full notes + airport heuristic. Precedence:
 * 1. Log / CSV <code>personal</code> → personal
 * 2. “Time share” in route or passenger text → personal (ForeFlight tag wins over KGJT anchor)
 * 3. From or to is a business-anchor airport (e.g. Grand Junction) → business
 * 4. Business-oriented wording in combined text → business
 * 5. Otherwise → business (operating default)
 */
export function classifyLegForRollup(leg: FlightLeg): LegRollupClassification {
  if (leg.personalUse) {
    return { personal: true, reasons: ["log / CSV personal"] };
  }

  const text = combinedNarrative(leg);
  if (textIndicatesTimeShare(text)) {
    return { personal: true, reasons: ["notes / passengers: time share"] };
  }

  const from = normIcao(leg.fromIcao);
  const to = normIcao(leg.toIcao);
  if (BUSINESS_ANCHOR_ICAO.has(from)) {
    return { personal: false, reasons: [`business airport (departure ${from})`] };
  }
  if (BUSINESS_ANCHOR_ICAO.has(to)) {
    return { personal: false, reasons: [`business airport (arrival ${to})`] };
  }

  if (textSuggestsBusiness(text)) {
    return { personal: false, reasons: ["notes / passengers: business wording"] };
  }

  return { personal: false, reasons: ["default (business)"] };
}

export function rollupClassificationSummary(c: LegRollupClassification): string {
  return c.reasons.join(" · ");
}
